/**
 * Depth of field (WebGPU / TSL).
 */

import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { float, max, mix, negate, smoothstep, sub, uniform } from 'three/tsl';

import { type EffectContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, type DepthOfFieldEffectConfig, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';

import { WebGPUOnlyEffect } from './WebGPUOnlyEffectBase.js';

type DepthOfFieldRuntimeNode = WebGPUEffectNode & {
  dofRuntime: {
    focusDistance: { value: number };
    focalLength: { value: number };
    bokehScale: { value: number };
  };
};

export class DepthOfFieldEffect extends WebGPUOnlyEffect<DepthOfFieldEffectConfig> {
  override readonly type = PostProcessPass.DepthOfField;
  override readonly order = PostProcessRenderOrder.DepthOfField;

  override getDefaultConfig(): DepthOfFieldEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.DepthOfField] };
  }

  override createWebGPUNode(config: DepthOfFieldEffectConfig, _context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const focusDistanceNode = uniform(config.focusDistance ?? defaults.focusDistance ?? 500);
    const focalLengthNode = uniform(config.focalLength ?? defaults.focalLength ?? 200);
    const bokehScaleNode = uniform(config.bokehScale ?? defaults.bokehScale ?? 10);
    const farOnly = config.farOnly ?? defaults.farOnly ?? true;
    const effectNode: WebGPUEffectNode = {
      node: null as any,
      compose: (outputNode, ctx) => {
        const viewZNode = ctx.scenePass.getViewZNode?.() ?? ctx.depthNode;
        const dofNode = dof(
          outputNode as any,
          viewZNode as any,
          focusDistanceNode as any,
          focalLengthNode as any,
          bokehScaleNode as any
        ) as any;

        if (!farOnly) {
          return dofNode;
        }

        // Same signed distance convention as `DepthOfFieldNode` (positive = farther than focus).
        const signedDist = sub(negate(viewZNode as any), focusDistanceNode);
        const blendBand = max(float(0.01), focalLengthNode.mul(0.02));
        const farBlend = smoothstep(float(0), blendBand, signedDist);
        return mix(outputNode as any, dofNode, farBlend) as any;
      },
    };
    (effectNode as DepthOfFieldRuntimeNode).dofRuntime = {
      focusDistance: focusDistanceNode,
      focalLength: focalLengthNode,
      bokehScale: bokehScaleNode,
    };
    return effectNode;
  }

  override applyWebGPUConfig(node: WebGPUEffectNode, config: DepthOfFieldEffectConfig): void {
    const runtimeNode = node as DepthOfFieldRuntimeNode;
    if (!runtimeNode.dofRuntime) return;
    const defaults = this.getDefaultConfig();
    runtimeNode.dofRuntime.focusDistance.value = config.focusDistance ?? defaults.focusDistance ?? 500;
    runtimeNode.dofRuntime.focalLength.value = config.focalLength ?? defaults.focalLength ?? 200;
    runtimeNode.dofRuntime.bokehScale.value = config.bokehScale ?? defaults.bokehScale ?? 10;
  }

  override requiresRebuild(oldConfig: DepthOfFieldEffectConfig, newConfig: DepthOfFieldEffectConfig): boolean {
    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.order !== newConfig.order ||
      oldConfig.type !== newConfig.type ||
      oldConfig.id !== newConfig.id ||
      oldConfig.farOnly !== newConfig.farOnly
    );
  }
}
